'use client';

import { useCallback } from 'react';
import { useTranslations } from 'next-intl';
import type {
  BodyScrollEvent,
  ColDef,
  GetRowIdParams,
  GridApi,
  GridReadyEvent,
  RowClassParams,
  RowClickedEvent,
  RowStyle,
  SortChangedEvent,
} from 'ag-grid-community';
import { Box, HStack } from '@terra/ui-styled-system';
import {
  MetaDefault,
  Spinner,
  Table,
  type GroupHeaderRowBase,
} from '@terra/ui-design-system';
import { isRowClickDisabledTarget } from '../tabs/rowClickBlocker';
import {
  LIST_PAGE_GROUP_HEADER_ROW_CLASS,
  LIST_PAGE_TABLE_HEADER_CSS,
  LIST_PAGE_TABLE_HEADER_HEIGHT_PX,
  LIST_PAGE_TABLE_THEME_PARAMS,
  listPageGroupHeaderStickyClassName,
} from './list-page-table-theme';
import {
  shouldLoadMoreListPageRows,
  type ListPageTableSort,
} from './list-page-infinite-table.model';

const ROW_HEIGHT_PX = 44;
const USER_SORT_SOURCE = 'uiColumnSorted';
const ITEM_ROW_STYLE: RowStyle = { cursor: 'pointer' };
const GROUP_ROW_STYLE: RowStyle = { cursor: 'pointer' };

type ListPageInfiniteTableProps<
  Item,
  Header extends GroupHeaderRowBase,
  SortColumn extends string,
> = {
  tableKey: string;
  columnDefs: ColDef<Item | Header>[];
  rows: Array<Item | Header>;
  getItemRowId: (item: Item) => string;
  isGroupHeaderRow: (row: Item | Header | null | undefined) => row is Header;
  loadMoreRowIndex: number | null;
  sort: ListPageTableSort<SortColumn>;
  isSortColumn: (colId: string) => colId is SortColumn;
  isLoading: boolean;
  isFetchingMore: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
  onSortChange: (sort: ListPageTableSort<SortColumn>) => void;
  onToggleGroup: (groupKey: string) => void;
  onOpenItem: (item: Item) => void;
};

const DEFAULT_COL_DEF = {
  sortable: false,
  filter: false,
  resizable: true,
  lockPinned: true,
  sortingOrder: ['asc' as const, 'desc' as const, null],
};

export function ListPageInfiniteTable<
  Item,
  Header extends GroupHeaderRowBase,
  SortColumn extends string,
>({
  tableKey,
  columnDefs,
  rows,
  getItemRowId,
  isGroupHeaderRow,
  loadMoreRowIndex,
  sort,
  isSortColumn,
  isLoading,
  isFetchingMore,
  hasMore,
  onLoadMore,
  onSortChange,
  onToggleGroup,
  onOpenItem,
}: ListPageInfiniteTableProps<Item, Header, SortColumn>) {
  const t = useTranslations('proxy.assets.table');
  const getRowId = useCallback(
    (params: GetRowIdParams<Item | Header>) =>
      isGroupHeaderRow(params.data)
        ? params.data.id
        : getItemRowId(params.data),
    [getItemRowId, isGroupHeaderRow],
  );
  const getRowStyle = useCallback(
    (params: RowClassParams<Item | Header>): RowStyle =>
      isGroupHeaderRow(params.data) ? GROUP_ROW_STYLE : ITEM_ROW_STYLE,
    [isGroupHeaderRow],
  );
  const getRowClass = useCallback(
    (params: RowClassParams<Item | Header>) =>
      isGroupHeaderRow(params.data)
        ? LIST_PAGE_GROUP_HEADER_ROW_CLASS
        : undefined,
    [isGroupHeaderRow],
  );
  const loadMoreIfNearEnd = useCallback(
    (api: GridApi<Item | Header>) => {
      if (
        shouldLoadMoreListPageRows({
          lastDisplayedRowIndex: api.getLastDisplayedRowIndex(),
          loadMoreRowIndex,
          hasMore,
          isFetchingMore,
        })
      ) {
        onLoadMore();
      }
    },
    [hasMore, isFetchingMore, loadMoreRowIndex, onLoadMore],
  );
  const handleGridReady = useCallback(
    (event: GridReadyEvent<Item | Header>) => {
      event.api.applyColumnState({
        state: sort ? [{ colId: sort.colId, sort: sort.sort }] : [],
        defaultState: { sort: null },
      });
    },
    [sort],
  );
  const handleSortChanged = useCallback(
    (event: SortChangedEvent<Item | Header>) => {
      if (event.source !== USER_SORT_SOURCE) {
        return;
      }

      const sortedColumn = event.api
        .getColumnState()
        .find((column) => column.sort === 'asc' || column.sort === 'desc');

      onSortChange(
        sortedColumn && isSortColumn(sortedColumn.colId)
          ? {
              colId: sortedColumn.colId,
              sort: sortedColumn.sort === 'desc' ? 'desc' : 'asc',
            }
          : null,
      );
    },
    [isSortColumn, onSortChange],
  );
  const handleRowClicked = useCallback(
    (event: RowClickedEvent<Item | Header>) => {
      if (!event.data || isRowClickDisabledTarget(event.event)) {
        return;
      }

      if (isGroupHeaderRow(event.data)) {
        onToggleGroup(event.data.groupKey);

        return;
      }

      onOpenItem(event.data);
    },
    [isGroupHeaderRow, onOpenItem, onToggleGroup],
  );

  return (
    <Box
      flex={1}
      w="full"
      h="full"
      minH={0}
      overflow="hidden"
      position="relative"
      className={listPageGroupHeaderStickyClassName}
      css={LIST_PAGE_TABLE_HEADER_CSS}
    >
      <Table
        key={tableKey}
        columnDefs={columnDefs}
        rowData={rows}
        getRowId={getRowId}
        rowHeight={ROW_HEIGHT_PX}
        headerHeight={LIST_PAGE_TABLE_HEADER_HEIGHT_PX}
        noBorder={true}
        fontSize={12}
        headerFontSize={12}
        themeParams={LIST_PAGE_TABLE_THEME_PARAMS}
        animateRows={false}
        defaultColDef={DEFAULT_COL_DEF}
        suppressCellFocus={true}
        suppressMultiSort={true}
        hideColumnControls={true}
        fitColumnsOnGridSizeChanged={false}
        loading={isLoading}
        onGridReady={handleGridReady}
        onSortChanged={handleSortChanged}
        onBodyScroll={(event: BodyScrollEvent<Item | Header>) =>
          loadMoreIfNearEnd(event.api)
        }
        onRowDataUpdated={(event) => loadMoreIfNearEnd(event.api)}
        onRowClicked={handleRowClicked}
        getRowStyle={getRowStyle}
        getRowClass={getRowClass}
        suppressNoRowsOverlay
      />
      {isFetchingMore ? (
        <HStack
          position="absolute"
          bottom={3}
          left="50%"
          transform="translateX(-50%)"
          gap={2}
          px={3}
          py={1.5}
          borderRadius="full"
          bg="bg.default"
          boxShadow="sm"
          pointerEvents="none"
        >
          <Spinner size="sm" />
          <MetaDefault color="fg.muted">{t('loadingMore')}</MetaDefault>
        </HStack>
      ) : null}
    </Box>
  );
}
